<template>
    <div>
        <div class="p-6 pb-0">
            <BreadCrumbs :navigation="navigation" />
        </div>

        <!-- Header ordine contratto -->
        <div class="p-6 pb-0">
            <div class="bg-white border border-gray-200 rounded-lg p-4 flex flex-wrap gap-6 text-sm">
                <div>
                    <span class="text-gray-500 block text-xs uppercase tracking-wide">Ordine contratto</span>
                    <span class="font-semibold text-gray-800">{{ ordine.InternalOrdNo }}</span>
                </div>
                <div>
                    <span class="text-gray-500 block text-xs uppercase tracking-wide">Cliente</span>
                    <span class="font-semibold text-gray-800">{{ ordine.CompanyName || ordine.Customer || '-' }}</span>
                </div>
                <div>
                    <span class="text-gray-500 block text-xs uppercase tracking-wide">Data ordine</span>
                    <span class="font-semibold text-gray-800">{{ formatDate(ordine.OrderDate) }}</span>
                </div>
                <div v-if="ordine.job_description">
                    <span class="text-gray-500 block text-xs uppercase tracking-wide">Commessa</span>
                    <span class="font-semibold text-gray-800">{{ ordine.job_description }}</span>
                </div>
            </div>
        </div>

        <div class="p-6 flex gap-6">
            <!-- Colonna sinistra: righe contratto -->
            <div class="w-1/3 flex-shrink-0">
                <h2 class="text-base font-semibold text-gray-700 mb-3">Righe contratto</h2>
                <div class="space-y-2">
                    <!-- Riepilogo contratto -->
                    <div
                        @click="showRiepilogo = true"
                        :class="[
                            'border rounded-lg p-3 cursor-pointer transition-colors text-sm',
                            showRiepilogo
                                ? 'border-indigo-500 bg-indigo-50'
                                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                        ]"
                    >
                        <div class="flex items-center gap-2">
                            <svg class="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                            </svg>
                            <div class="font-medium text-indigo-700">Riepilogo contratto</div>
                        </div>
                        <div class="mt-1">
                            <span class="text-xs bg-indigo-100 text-indigo-700 rounded px-1.5 py-0.5">
                                {{ localSeries.length }} serie totali
                            </span>
                        </div>
                    </div>

                    <!-- Serie generali contratto (senza row_id) -->
                    <div
                        @click="selectGenerali"
                        :class="[
                            'border rounded-lg p-3 cursor-pointer transition-colors text-sm',
                            selectedRigaKey === null && !showRiepilogo
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                        ]"
                    >
                        <div class="font-medium text-gray-500 italic">Attività generali contratto</div>
                        <div class="mt-1 flex gap-1 flex-wrap">
                            <span class="text-xs bg-gray-100 text-gray-600 rounded px-1.5 py-0.5">
                                {{ seriesGenerali.length }} serie
                            </span>
                            <span class="text-xs bg-gray-100 text-gray-600 rounded px-1.5 py-0.5">
                                {{ totalActivitiesGenerali }} attività
                            </span>
                        </div>
                    </div>

                    <div
                        v-for="riga in righe"
                        :key="riga.row_id"
                        @click="selectRiga(riga)"
                        :class="[
                            'border rounded-lg p-3 cursor-pointer transition-colors text-sm',
                            selectedRigaKey === riga.row_id && !showRiepilogo
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                        ]"
                    >
                        <div class="font-medium text-gray-800 truncate">{{ riga.Description || riga.Item }}</div>
                        <div class="text-xs text-gray-500 mt-1">
                            <span class="font-mono">{{ riga.Item }}</span>
                            <span
                                v-if="riga.ConfirmedDeliveryDate"
                                class="ml-2"
                                :class="dateStatus(riga.ConfirmedDeliveryDate)"
                            >· consegna {{ formatDate(riga.ConfirmedDeliveryDate) }}</span>
                        </div>
                        <div class="mt-1.5 flex flex-wrap gap-1">
                            <span class="text-xs bg-blue-100 text-blue-700 rounded px-1.5 py-0.5">
                                {{ seriesPerRiga(riga).length }} serie
                            </span>
                            <span class="text-xs bg-indigo-100 text-indigo-700 rounded px-1.5 py-0.5">
                                {{ totalActivitiesForRiga(riga) }} attività
                            </span>
                            <span v-if="nextDateForRiga(riga)" class="text-xs bg-green-100 text-green-700 rounded px-1.5 py-0.5">
                                prossima {{ formatDate(nextDateForRiga(riga)) }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Colonna destra: serie -->
            <div class="flex-1 min-w-0">
                <!-- Vista riepilogo -->
                <template v-if="showRiepilogo">
                    <h2 class="text-base font-semibold text-indigo-700 mb-4">Riepilogo contratto {{ ordine.InternalOrdNo }}</h2>
                    <div v-if="localSeries.length === 0" class="text-center py-12 text-gray-400 text-sm border border-dashed border-gray-200 rounded-lg">
                        Nessuna serie per questo contratto.
                    </div>
                    <div v-else class="space-y-6">
                        <div v-for="gruppo in seriesRaggruppate" :key="gruppo.label">
                            <div v-if="gruppo.series.length > 0" class="flex items-center gap-3 mb-2">
                                <div class="h-px flex-1 bg-gray-200"></div>
                                <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{{ gruppo.label }}</span>
                                <div class="h-px flex-1 bg-gray-200"></div>
                            </div>
                            
                            <div class="space-y-3">
                                <div v-for="serie in gruppo.series" :key="serie.id">
                                    <SeriesCard
                                        :serie="serie"
                                        :can-manage="canManageActivities"
                                        :expanded="expandedSeriesIds.includes(serie.id)"
                                        @toggle-expand="toggleExpand(serie.id)"
                                        @edit="editSerie(serie)"
                                        @add-date="aggiungiData(serie)"
                                        @suspend="sospendi(serie)"
                                        @resume="riattiva(serie)"
                                        @regenerate="rigeneraFuture(serie)"
                                        @delete="eliminaSerie(serie)"
                                        @delete-date="eliminaData(serie, $event)"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </template>

                <!-- Vista normale per riga selezionata -->
                <template v-else>
                    <div class="flex items-center justify-between mb-4">
                        <h2 class="text-base font-semibold text-gray-700">
                            <span v-if="selectedRigaKey !== null">
                                Serie — {{ selectedRigaLabel }}
                            </span>
                            <span v-else>Serie generali contratto</span>
                        </h2>
                        <button v-if="canManageActivities" @click="nuovaSerie" class="p93__btn_primary text-sm">
                            + Nuova serie
                        </button>
                    </div>

                    <div v-if="visibleSeries.length === 0" class="text-center py-12 text-gray-400 text-sm border border-dashed border-gray-200 rounded-lg">
                        Nessuna serie.
                        <span v-if="canManageActivities"> Clicca "Nuova serie" per iniziare.</span>
                    </div>

                    <div class="space-y-3">
                        <div v-for="serie in visibleSeries" :key="serie.id">
                            <SeriesCard
                                :serie="serie"
                                :can-manage="canManageActivities"
                                :expanded="expandedSeriesIds.includes(serie.id)"
                                @toggle-expand="toggleExpand(serie.id)"
                                @edit="editSerie(serie)"
                                @add-date="aggiungiData(serie)"
                                @suspend="sospendi(serie)"
                                @resume="riattiva(serie)"
                                @regenerate="rigeneraFuture(serie)"
                                @delete="eliminaSerie(serie)"
                                @delete-date="eliminaData(serie, $event)"
                            />
                        </div>
                    </div>
                </template>
            </div>
        </div>

        <!-- Modale Unificato Creazione / Modifica Serie -->
        <ModaleAttivita
            v-model="showModaleSerie"
            :serie="selectedSerieForModal"
            default-activity-type="periodic"
            :ordine="ordine"
            :righe="righe"
            :default-row-id="selectedRigaKey"
            :departments="departments"
            :can-manage-activities="canManageActivities"
            :max-occurrences="maxOccurrences"
            @saved="onSerieSaved"
        />

        <!-- Drawer Aggiungi singola data manuale a serie esistente -->
        <transition name="action-panel-fade">
            <div
                v-if="showAddDateDrawer"
                class="fixed inset-0 z-50 bg-gray-900 bg-opacity-40 mt-10"
                @click="chiudiAddDateDrawer"
            >
                <transition name="action-panel-slide">
                    <aside
                        v-if="showAddDateDrawer"
                        class="absolute right-0 mt-10 h-[calc(100vh-5rem)] w-full max-w-md bg-white shadow-2xl flex flex-col rounded-tl-xl"
                        @click.stop
                    >
                        <!-- Header -->
                        <div class="border-b border-gray-200 px-6 py-5 bg-white">
                            <div class="flex items-start justify-between gap-4">
                                <div class="min-w-0">
                                    <span class="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Aggiungi occorrenza
                                    </span>
                                    <h2 class="text-lg font-semibold text-gray-900 mt-1 truncate">
                                        {{ selectedSerieForAddDate ? selectedSerieForAddDate.title : 'Nuova data' }}
                                    </h2>
                                </div>
                                <button
                                    type="button"
                                    @click="chiudiAddDateDrawer"
                                    class="rounded-full p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                                >
                                    <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <!-- Body -->
                        <div class="flex-1 overflow-y-auto px-6 py-6 bg-white space-y-4">
                            <div v-if="addDateError" class="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                                {{ addDateError }}
                            </div>

                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Data *</label>
                                <input
                                    v-model="addDateForm.scheduled_date"
                                    type="date"
                                    class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Ora inizio (opzionale)</label>
                                <input
                                    v-model="addDateForm.start_time"
                                    type="time"
                                    class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Durata ore (opzionale)</label>
                                <input
                                    v-model.number="addDateForm.duration_hours"
                                    type="number"
                                    min="0.25"
                                    step="0.25"
                                    class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="es. 2.5"
                                />
                            </div>

                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Note (opzionale)</label>
                                <textarea
                                    v-model="addDateForm.notes"
                                    rows="3"
                                    class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Note opzionali"
                                ></textarea>
                            </div>
                        </div>

                        <!-- Footer -->
                        <div class="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-end gap-3">
                            <button
                                type="button"
                                @click="chiudiAddDateDrawer"
                                class="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors text-sm"
                            >
                                Annulla
                            </button>
                            <button
                                type="button"
                                @click="salvaDataManuale"
                                :disabled="savingAddDate"
                                class="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition-colors text-sm disabled:opacity-50"
                            >
                                {{ savingAddDate ? 'Salvataggio...' : 'Aggiungi data' }}
                            </button>
                        </div>
                    </aside>
                </transition>
            </div>
        </transition>
    </div>
</template>

<script>
import { Link, router } from '@inertiajs/vue3';
import axios from 'axios';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import SeriesCard from '@/Components/SeriesCard.vue';
import ModaleAttivita from '@/Pages/Modules/attivita/Attivita/ModaleAttivita.vue';

export default {
    name: 'GestioneAttivitaContratto',

    props: {
        ordine: Object,
        righe: Array,
        series: Array,
        isManager: Boolean,
        canManageActivities: Boolean,
        departments: Array,
        workingHoursConfig: Object,
        maxOccurrences: { type: Number, default: 200 },
        slotHorizonDays: Number,
    },

    components: {
        Link,
        BreadCrumbs,
        SeriesCard,
        ModaleAttivita,
    },

    data() {
        return {
            localSeries: JSON.parse(JSON.stringify(this.series || [])),
            selectedRigaKey: this.righe && this.righe.length > 0 ? this.righe[0].row_id : null,
            showRiepilogo: false,
            expandedSeriesIds: [],

            // Modale Unificato Serie
            showModaleSerie: false,
            selectedSerieForModal: null,

            // Drawer singola data
            showAddDateDrawer: false,
            selectedSerieForAddDate: null,
            addDateForm: this.emptyAddDateForm(),
            addDateError: null,
            savingAddDate: false,
        };
    },

    watch: {
        series(val) {
            this.localSeries = JSON.parse(JSON.stringify(val || []));
        },
    },

    computed: {
        navigation() {
            return [
                { name: this.$t('ui.dashboard'), href: route('dashboard') },
                { name: this.$t('modules.attivita'), href: route('modules.home', 'attivita') },
                { name: 'Contratti', href: route('modules.attivita.contratti') },
                { name: 'Contratto ' + this.ordine.InternalOrdNo },
            ];
        },
        selectedRiga() {
            if (this.selectedRigaKey === null) return null;
            return this.righe.find(r => r.row_id === this.selectedRigaKey) || null;
        },
        selectedRigaLabel() {
            return this.selectedRiga ? (this.selectedRiga.Description || this.selectedRiga.Item) : '';
        },
        seriesGenerali() {
            return this.localSeries.filter(s => !s.row_id);
        },
        totalActivitiesGenerali() {
            return this.seriesGenerali.reduce((sum, s) => sum + (s.activities_count || 0), 0);
        },
        visibleSeries() {
            if (this.selectedRigaKey === null) {
                return this.seriesGenerali;
            }
            return this.localSeries.filter(s => s.row_id === this.selectedRigaKey);
        },
        seriesRaggruppate() {
            const gruppi = (this.righe || []).map(riga => ({
                label: riga.Description || riga.Item,
                series: this.localSeries.filter(s => s.row_id === riga.row_id),
            }));
            gruppi.push({ label: 'Attività generali contratto', series: this.seriesGenerali });
            return gruppi;
        },
    },

    methods: {
        emptyAddDateForm() {
            return {
                scheduled_date: '',
                start_time: '',
                duration_hours: null,
                notes: '',
            };
        },

        seriesPerRiga(riga) {
            return this.localSeries.filter(s => s.row_id === riga.row_id);
        },

        totalActivitiesForRiga(riga) {
            return this.seriesPerRiga(riga).reduce((sum, s) => sum + (s.activities_count || 0), 0);
        },

        nextDateForRiga(riga) {
            const dates = this.seriesPerRiga(riga).map(s => s.next_date).filter(Boolean).sort();
            return dates[0] || null;
        },

        selectRiga(riga) {
            this.showRiepilogo = false;
            this.selectedRigaKey = riga.row_id;
        },

        selectGenerali() {
            this.showRiepilogo = false;
            this.selectedRigaKey = null;
        },

        toggleExpand(seriesId) {
            const idx = this.expandedSeriesIds.indexOf(seriesId);
            if (idx === -1) this.expandedSeriesIds.push(seriesId);
            else this.expandedSeriesIds.splice(idx, 1);
        },

        nuovaSerie() {
            this.selectedSerieForModal = null;
            this.showModaleSerie = true;
        },

        editSerie(serie) {
            this.selectedSerieForModal = serie;
            this.showModaleSerie = true;
        },

        onSerieSaved() {
            this.reloadPage();
        },

        aggiungiData(serie) {
            this.selectedSerieForAddDate = serie;
            this.addDateForm = this.emptyAddDateForm();
            this.addDateForm.start_time = this.normalizeTimeInput(serie.default_start_time);
            this.addDateForm.duration_hours = serie.default_duration_hours ? Number(serie.default_duration_hours) : null;
            this.addDateError = null;
            this.showAddDateDrawer = true;
        },

        chiudiAddDateDrawer() {
            if (this.savingAddDate) return;
            this.showAddDateDrawer = false;
            this.selectedSerieForAddDate = null;
            this.addDateForm = this.emptyAddDateForm();
            this.addDateError = null;
        },

        async salvaDataManuale() {
            if (!this.addDateForm.scheduled_date) {
                this.addDateError = 'La data è obbligatoria.';
                return;
            }

            if (
                this.addDateForm.duration_hours !== null &&
                this.addDateForm.duration_hours !== '' &&
                Number(this.addDateForm.duration_hours) <= 0
            ) {
                this.addDateError = 'La durata deve essere maggiore di zero.';
                return;
            }

            this.savingAddDate = true;
            this.addDateError = null;

            try {
                await axios.post(
                    route('modules.attivita.series.dates.store', {
                        series: this.selectedSerieForAddDate.id,
                    }),
                    {
                        date: this.addDateForm.scheduled_date,
                        start_time: this.addDateForm.start_time || null,
                        duration_hours: this.nullableNumber(this.addDateForm.duration_hours),
                        notes: this.addDateForm.notes?.trim() || null,
                    }
                );

                this.chiudiAddDateDrawer();
                this.reloadPage();
            } catch (error) {
                this.addDateError = this.extractErrorMessage(error, 'Errore durante il salvataggio della data.');
            } finally {
                this.savingAddDate = false;
            }
        },

        async sospendi(serie) {
            if (!window.confirm(`Vuoi sospendere la serie "${serie.title}"? Le attività già generate non verranno eliminate.`)) {
                return;
            }
            await this.executeSeriesAction('modules.attivita.series.suspend', serie, {}, 'Impossibile sospendere la serie.');
        },

        async riattiva(serie) {
            if (!window.confirm(`Vuoi riattivare la serie "${serie.title}"?`)) {
                return;
            }
            await this.executeSeriesAction('modules.attivita.series.resume', serie, {}, 'Impossibile riattivare la serie.');
        },

        async rigeneraFuture(serie) {
            if (!window.confirm(`Vuoi rigenerare le occorrenze future della serie "${serie.title}"?\n\nLe attività completate, in lavorazione o personalizzate non verranno modificate.`)) {
                return;
            }
            await this.executeSeriesAction('modules.attivita.series.regenerate-future', serie, {}, 'Impossibile rigenerare le attività future.');
        },

        async eliminaSerie(serie) {
            if (!window.confirm(`Vuoi eliminare la serie "${serie.title}"?\n\nSaranno eliminate solo le attività future modificabili. Le attività già iniziate, completate o consuntivate verranno mantenute.`)) {
                return;
            }

            try {
                await axios.delete(route('modules.attivita.series.destroy', { series: serie.id }));
                this.expandedSeriesIds = this.expandedSeriesIds.filter(id => id !== serie.id);
                this.reloadPage();
            } catch (error) {
                window.alert(this.extractErrorMessage(error, 'Impossibile eliminare la serie.'));
            }
        },

        async eliminaData(serie, date) {
            if (!window.confirm(`Vuoi eliminare l’occorrenza del ${this.formatDate(date.scheduled_date)}?`)) {
                return;
            }

            try {
                await axios.delete(
                    route('modules.attivita.series.dates.destroy', {
                        series: serie.id,
                        date: date.id,
                    })
                );
                this.reloadPage();
            } catch (error) {
                window.alert(this.extractErrorMessage(error, 'Impossibile eliminare l’occorrenza.'));
            }
        },

        async executeSeriesAction(routeName, serie, payload = {}, fallbackMessage = 'Errore durante l\'azione.') {
            try {
                await axios.post(route(routeName, { series: serie.id }), payload);
                this.reloadPage();
            } catch (error) {
                window.alert(this.extractErrorMessage(error, fallbackMessage));
            }
        },

        reloadPage() {
            router.reload({
                only: ['series', 'righe', 'departments', 'assignableUsers'],
                preserveScroll: true,
                preserveState: true,
            });
        },

        formatDate(value) {
            if (!value) return '-';
            const date = this.parseLocalDate(value);
            if (!date || Number.isNaN(date.getTime())) return value;
            return new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
        },

        dateStatus(value) {
            if (!value) return null;
            const date = this.parseLocalDate(value);
            if (!date) return null;
            date.setHours(0, 0, 0, 0);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (date.getTime() === today.getTime()) return 'oggi';
            if (date < today) return 'scaduto';
            return null;
        },

        parseLocalDate(value) {
            if (!value) return null;
            if (value instanceof Date) return new Date(value.getTime());
            const normalized = String(value).substring(0, 10);
            const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            if (match) {
                const year = Number(match[1]);
                const month = Number(match[2]) - 1;
                const day = Number(match[3]);
                const date = new Date(year, month, day);
                if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) return null;
                return date;
            }
            const fallbackDate = new Date(value);
            return Number.isNaN(fallbackDate.getTime()) ? null : fallbackDate;
        },

        normalizeDateInput(value) {
            const date = this.parseLocalDate(value);
            return date ? this.toDateInputValue(date) : '';
        },

        toDateInputValue(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        },

        normalizeTimeInput(value) {
            if (!value) return '';
            const time = String(value);
            if (/^\d{2}:\d{2}/.test(time)) return time.substring(0, 5);
            return '';
        },

        nullableNumber(value) {
            if (value === null || value === undefined || value === '') return null;
            const number = Number(value);
            return Number.isFinite(number) ? number : null;
        },

        extractErrorMessage(error, fallbackMessage) {
            const responseData = error?.response?.data;
            if (responseData?.message) return responseData.message;
            if (responseData?.error) return responseData.error;
            if (responseData?.errors) {
                const messages = Object.values(responseData.errors).flat().filter(Boolean);
                if (messages.length > 0) return messages.join(' ');
            }
            return fallbackMessage;
        },
    },
};
</script>

<style scoped>
.scaduto {
    color: #dc2626;
    font-weight: 600;
    background-color: #ffe6e6;
    padding: 2px 4px;
    border-radius: 4px;
}
.oggi {
    color: #3225eb;
    font-weight: 600;
    background-color: #e6e6ff;
    padding: 2px 4px;
    border-radius: 4px;
}

.action-panel-fade-enter-active,
.action-panel-fade-leave-active {
    transition: opacity 0.2s ease;
}

.action-panel-fade-enter-from,
.action-panel-fade-leave-to {
    opacity: 0;
}

.action-panel-slide-enter-active,
.action-panel-slide-leave-active {
    transition: transform 0.25s ease;
}

.action-panel-slide-enter-from,
.action-panel-slide-leave-to {
    transform: translateX(100%);
}

@media (max-width: 1024px) {
    aside {
        max-width: 85vw;
    }
}

@media (max-width: 768px) {
    aside {
        max-width: 100vw;
        border-radius: 0;
    }
}
</style>
